import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { AppErrors, BadRequestError, NotFoundError } from '@fitness-force/models';
import type {
  BlackoutConfig,
  BlackoutConfigListResponse,
  CreateBlackoutConfigRequest,
  UpdateBlackoutConfigRequest,
} from '@fitness-force/models';

@Injectable({
  providedIn: 'root'
})
export class BlackoutConfigService {
  private http = inject(HttpClient);

  BASE_URL: string = environment.BASE_URL;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private handleError(error: Response): Observable<never> {
    if (error.status === 404) {
      return throwError(() => new NotFoundError(error));
    } else if (error.status === 400) {
      return throwError(() => new BadRequestError(error));
    }
    return throwError(() => new AppErrors(error));
  }

  getActiveBlackoutConfigs(tenantId: number): Observable<BlackoutConfigListResponse> {
    const params = new HttpParams().append('is_active', 'true');
    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.GET_BLACKOUT_CONFIGS
      .replace('{tenant_id}', tenantId.toString());

    return this.http.get<BlackoutConfigListResponse>(url, {
      headers: this.httpOptions.headers,
      params
    }).pipe(
      map((res: unknown) => res as BlackoutConfigListResponse),
      catchError((error: Response) => this.handleError(error))
    );
  }

  listBlackoutConfigs(
    tenantId: number,
    filters?: { type?: 'Date' | 'Time'; is_active?: boolean }
  ): Observable<BlackoutConfigListResponse> {
    let params = new HttpParams();
    if (filters?.type !== undefined) {
      params = params.append('type', filters.type);
    }
    if (filters?.is_active !== undefined) {
      params = params.append('is_active', String(filters.is_active));
    }

    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.LIST_BLACKOUT_CONFIGS
      .replace('{tenant_id}', tenantId.toString());

    return this.http.get<BlackoutConfigListResponse>(url, {
      headers: this.httpOptions.headers,
      params
    }).pipe(
      map((res: unknown) => res as BlackoutConfigListResponse),
      catchError((error: Response) => this.handleError(error))
    );
  }

  getBlackoutConfig(tenantId: number, configId: number): Observable<BlackoutConfig> {
    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.GET_BLACKOUT_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{config_id}', configId.toString());

    return this.http.get<{ value: BlackoutConfig }>(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: { value: BlackoutConfig }) => res.value),
      catchError((error: Response) => this.handleError(error))
    );
  }

  createBlackoutConfig(
    tenantId: number,
    payload: CreateBlackoutConfigRequest
  ): Observable<unknown> {
    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.CREATE_BLACKOUT_CONFIG
      .replace('{tenant_id}', tenantId.toString());

    return this.http.post(url, payload, this.httpOptions).pipe(
      map((res: unknown) => res),
      catchError((error: Response) => this.handleError(error))
    );
  }

  updateBlackoutConfig(
    tenantId: number,
    configId: number,
    payload: UpdateBlackoutConfigRequest
  ): Observable<unknown> {
    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.UPDATE_BLACKOUT_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{config_id}', configId.toString());

    return this.http.put(url, payload, this.httpOptions).pipe(
      map((res: unknown) => res),
      catchError((error: Response) => this.handleError(error))
    );
  }

  markBlackoutActive(tenantId: number, configId: number): Observable<unknown> {
    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.ACTIVATE_BLACKOUT_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{config_id}', configId.toString());

    return this.http.post(url, {}, this.httpOptions).pipe(
      map((res: unknown) => res),
      catchError((error: Response) => this.handleError(error))
    );
  }

  markBlackoutInactive(tenantId: number, configId: number): Observable<unknown> {
    const url = this.BASE_URL + CONST_URL_SEGEMENTS.CAMPAIGN.DEACTIVATE_BLACKOUT_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{config_id}', configId.toString());

    return this.http.post(url, {}, this.httpOptions).pipe(
      map((res: unknown) => res),
      catchError((error: Response) => this.handleError(error))
    );
  }
}
